/**
 * App.js - 모바일 전용 React 애플리케이션 최상위 컴포넌트
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { LoadScript } from '@react-google-maps/api';
import { User as UserIcon } from 'lucide-react';
import AuthScreen from './components/auth/AuthScreen';
import AdminDashboard from './components/admin/AdminDashboard';
import MobileDashboard from './components/mobile/MobileDashboard';
import MobileCalendarView from './components/mobile/MobileCalendarView';
import MobileScheduleView from './components/mobile/MobileScheduleView';
import MobileGroupsView from './components/mobile/MobileGroupsView';
import MobileSettings from './components/mobile/MobileSettings';
import MobileFriendsView from './components/mobile/MobileFriendsView';
import InvitePage from './components/InvitePage';
import InstallPromptOverlay, { shouldShowInstallPrompt } from './components/InstallPromptOverlay';
import InstallGateScreen from './components/InstallGateScreen';
import { ToastProvider } from './contexts/ToastContext';
import { useAuth } from './hooks/useAuth';
import { useChat } from './hooks/useChat';
import { auth, messaging, messagingReady, FCM_VAPID_KEY } from './config/firebaseConfig';
import { getToken } from 'firebase/messaging';
import { API_BASE_URL } from './config/apiConfig';

const libraries = ['places', 'geometry']; // geometry: 인코딩 폴리라인 디코딩 (먼 거리 경로 직선 방지)
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Maps가 필요한 라우트만 LoadScript로 감싸는 컴포넌트
// /invite, /auth 등 Maps 불필요 라우트는 Google Maps 로드를 기다리지 않음
function MapLoadedRoutes({ isLoggedIn, user, isVoiceEnabled, setIsVoiceEnabled, installPrompt, onInstallPromptUsed }) {
   return (
      <LoadScript
         googleMapsApiKey={GOOGLE_API_KEY}
         libraries={libraries}
         language="ko"
         loadingElement={
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
               {localStorage.getItem('userProfileImage') ? (
                  <img src={localStorage.getItem('userProfileImage')} alt="로딩 중" style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover', marginBottom: '16px' }} />
               ) : (
                  <div style={{ width: '150px', height: '150px', borderRadius: '50%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                     <UserIcon size={80} color="#9ca3af" />
                  </div>
               )}
               <p style={{ color: '#666', fontSize: '14px' }}>로딩 중...</p>
            </div>
         }
      >
         <Routes>
            <Route path="/" element={
               isLoggedIn ? <MobileDashboard user={user} isVoiceEnabled={isVoiceEnabled} setIsVoiceEnabled={setIsVoiceEnabled} /> : <Navigate to="/auth" replace />
            } />
            <Route path="/schedule" element={
               isLoggedIn ? <MobileScheduleView user={user} isVoiceEnabled={isVoiceEnabled} setIsVoiceEnabled={setIsVoiceEnabled} /> : <Navigate to="/auth" replace />
            } />
            <Route path="/groups" element={
               (() => {
                  if (!isLoggedIn) {
                     // 리로드/킬 후 재시작 시 openRoom 파라미터 보존
                     const openRoom = new URLSearchParams(window.location.search).get('openRoom');
                     if (openRoom) localStorage.setItem('pendingOpenRoom', openRoom);
                     return <Navigate to="/auth" replace />;
                  }
                  return <MobileGroupsView user={user} isVoiceEnabled={isVoiceEnabled} setIsVoiceEnabled={setIsVoiceEnabled} />;
               })()
            } />
            <Route path="/calendar" element={
               isLoggedIn ? <MobileCalendarView user={user} isVoiceEnabled={isVoiceEnabled} setIsVoiceEnabled={setIsVoiceEnabled} /> : <Navigate to="/auth" replace />
            } />
            <Route path="/settings" element={
               isLoggedIn ? <MobileSettings user={user} installPrompt={installPrompt} onInstallPromptUsed={onInstallPromptUsed} /> : <Navigate to="/auth" replace />
            } />
            <Route path="/friends" element={
               isLoggedIn ? <MobileFriendsView user={user} /> : <Navigate to="/auth" replace />
            } />
         </Routes>
      </LoadScript>
   );
}

// ─────────────────────────────────────────────────────
// 모듈 레벨 SW 메시지 리스너: useEffect 이전에 등록
// 이유: 앱 시작 직후 (Firebase auth 미복원) 에 push 탭 시 useEffect 리스너가 아직 없음
// 이 리스너가 메시지를 캡처해 _pendingSwRoomId에 저장 → isLoggedIn=true 시 처리
// ─────────────────────────────────────────────────────
let _pendingSwRoomId = null;
// FCM 토큰 등록 동시 실행 방지 플래그
let _pushSubscribing = false;
if ('serviceWorker' in navigator) {
   navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'NAVIGATE_TO_ROOM') {
         const { roomId } = event.data;
         _pendingSwRoomId = roomId;
         if (roomId) localStorage.setItem('pendingOpenRoom', roomId);
         // SW에 수신 확인 (pendingNavigateRoomId 정리)
         navigator.serviceWorker.controller?.postMessage({ type: 'CLEAR_PENDING_NAVIGATE' });
      }
      // 새 SW 활성화 시 구 JS 번들로 실행 중인 페이지를 리로드 → 새 코드 적용
      if (event.data?.type === 'SW_UPDATED') {
         window.location.reload();
      }
   });
}

// 푸시 알림 클릭 시 /groups 외 다른 경로에 있을 때 /groups로 이동시키는 핸들러
// useNavigate는 Router 내부에서만 사용 가능하므로 별도 컴포넌트로 분리
function SWNavigationHandler({ isLoggedIn }) {
   const navigate = useNavigate();
   useEffect(() => {
      const onNeedGroupsNav = (e) => {
         const roomId = e.detail?.roomId;
         if (isLoggedIn) {
            const target = roomId ? `/groups?openRoom=${encodeURIComponent(roomId)}` : '/groups';
            navigate(target);
         }
      };
      window.addEventListener('swNeedGroupsNav', onNeedGroupsNav);
      return () => {
         window.removeEventListener('swNeedGroupsNav', onNeedGroupsNav);
      };
   }, [navigate, isLoggedIn]);
   return null;
}

// 관리자 계정 로그인 시 /admin으로 자동 이동
// user 데이터가 비동기로 로드되므로 /auth 라우트 redirect 시점엔 user가 null일 수 있음
// → user 로드 완료 후 role 확인하여 이동
function AdminRedirectHandler({ isLoggedIn, user }) {
   const navigate = useNavigate();
   useEffect(() => {
      if (!isLoggedIn || !user) return;
      if (user.role === 'admin' && window.location.pathname !== '/admin') {
         navigate('/admin', { replace: true });
      }
   }, [isLoggedIn, user, navigate]);
   return null;
}

// 오버레이 닫힌 후 초대 방으로 이동 (오버레이가 열려있는 동안 대기)
function PendingCodeNavigator({ code, overlayVisible, onNavigated }) {
   const navigate = useNavigate();
   useEffect(() => {
      if (!code) return;
      if (overlayVisible) return; // 오버레이 닫힐 때까지 대기
      onNavigated();
      navigate(`/invite/${code}`, { replace: true });
   }, [code, overlayVisible, navigate, onNavigated]);
   return null;
}


function App() {
   const { isLoggedIn, user, handleLoginSuccess: _handleLoginSuccess } = useAuth();
   const [eventAddedKey, setEventAddedKey] = useState(0);
   const [isVoiceRecognitionEnabled, setIsVoiceRecognitionEnabled] = useState(() => {
      const saved = localStorage.getItem('voiceRecognitionEnabled');
      return saved !== null ? JSON.parse(saved) : true;
   });
   const [eventActions] = useState(null);
   const roomListSocketRef = useRef(null); // 방 목록 실시간 업데이트용 소켓
   const userRef = useRef(user); // 최신 user 참조 (소켓 콜백용)
   const _updateBadgeTimer = useRef(null); // updateBadge 디바운스 타이머
   const [installPrompt, setInstallPrompt] = useState(null); // PWA 설치 프롬프트 (share target 등록에 필요)
   const [showInstallOverlay, setShowInstallOverlay] = useState(false); // 회원가입 후 앱 설치 유도
   const [showInstallGate, setShowInstallGate] = useState(false); // 초대 가입 후 강제 설치 게이트
   const [pendingJoinCode, setPendingJoinCode] = useState(null); // 오버레이 닫힌 뒤 입장할 초대 코드
   const [showNotifPrompt, setShowNotifPrompt] = useState(false); // 알림 권한 안내 모달
   const { handleChatMessage } = useChat(isLoggedIn, setEventAddedKey, eventActions);

   // 로그인 성공 시 모바일이면 즉시 게이트 표시 (isLoggedIn effect 타이밍 보완)
   const handleLoginSuccess = useCallback((userData, loginType) => {
      _handleLoginSuccess(userData, loginType);
      const isMobile = /Android/.test(navigator.userAgent) || (/iPhone|iPod/.test(navigator.userAgent) && !/iPad/.test(navigator.userAgent));
      const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
      if (isMobile && !isStandaloneMode) {
         setShowInstallGate(true);
      }
   }, [_handleLoginSuccess]);

   // SW 알림 방 이동 헬퍼: 현재 경로에 따라 swNavigateToRoom 또는 swNeedGroupsNav 발송
   const dispatchSwNavigation = useCallback((roomId) => {
      if (!roomId) return;
      if (window.location.pathname === '/groups') {
         window.dispatchEvent(new CustomEvent('swNavigateToRoom', { detail: { roomId } }));
      } else {
         window.dispatchEvent(new CustomEvent('swNeedGroupsNav', { detail: { roomId } }));
      }
   }, []);

   const handleCodeNavigated = useCallback(() => setPendingJoinCode(null), []);

   // 로그인 직후: 설치 게이트 / 오버레이 / 방 입장 결정
   useEffect(() => {
      if (!isLoggedIn) return;
      const pendingCode = localStorage.getItem('pendingInviteCode');
      localStorage.removeItem('deferredInstall');

      // iPad/태블릿/데스크탑은 제외 — iPhone·Android 폰만 게이트 적용
      const isMobile = /Android/.test(navigator.userAgent) || (/iPhone|iPod/.test(navigator.userAgent) && !/iPad/.test(navigator.userAgent));
      const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

      if (isMobile && !isStandaloneMode) {
         // 모든 모바일 사용자: PWA 미설치 → 설치 게이트
         setShowInstallGate(true);
      } else if (pendingCode) {
         // standalone 또는 데스크탑 + 초대 코드 → 바로 방 입장
         localStorage.removeItem('pendingInviteCode');
         setPendingJoinCode(pendingCode);
      } else {
         localStorage.removeItem('pendingInviteCode');
         if (!isMobile && shouldShowInstallPrompt()) {
            // 데스크탑 첫 로그인 → 기존 오버레이
            setShowInstallOverlay(true);
         }
      }
   }, [isLoggedIn]); // eslint-disable-line

   // 설치 게이트 표시 중 → standalone 전환 감지 (display-mode 변경)
   // Android: PWA 설치 후 Chrome이 standalone으로 전환할 때 처리
   useEffect(() => {
      if (!showInstallGate || !isLoggedIn) return;
      const mq = window.matchMedia('(display-mode: standalone)');
      const handleChange = (e) => {
         if (!e.matches) return;
         const pendingCode = localStorage.getItem('pendingInviteCode');
         localStorage.removeItem('inviteUserRequiresPWA');
         setShowInstallGate(false);
         if (pendingCode) {
            localStorage.removeItem('pendingInviteCode');
            setPendingJoinCode(pendingCode);
         }
      };
      mq.addEventListener('change', handleChange);
      return () => mq.removeEventListener('change', handleChange);
   }, [showInstallGate, isLoggedIn]);

   // appinstalled 이벤트: PWA가 홈 화면에 추가됨
   // 주의: 이 이벤트는 브라우저(네이버 등)에서도 발생 → 아직 standalone이 아님
   // → 게이트를 여기서 닫으면 브라우저에서 게이트가 사라지는 버그 발생
   // → 게이트 닫기는 display-mode change 리스너 또는 PWA 재시작(isLoggedIn effect)에 맡김
   useEffect(() => {
      const handleAppInstalled = () => {
         localStorage.removeItem('inviteUserRequiresPWA');
         // pendingInviteCode는 PWA 열릴 때 isLoggedIn effect 또는 display-mode change에서 처리
      };
      window.addEventListener('appinstalled', handleAppInstalled);
      return () => window.removeEventListener('appinstalled', handleAppInstalled);
   }, []); // eslint-disable-line

   // isLoggedIn이 true가 된 시점에 pending SW 방 이동 처리
   // (앱 시작 직후 push 탭 → Firebase 미복원 → 모듈레벨 리스너가 저장 → 여기서 처리)
   useEffect(() => {
      if (!isLoggedIn) return;
      const pendingRoomId = _pendingSwRoomId || localStorage.getItem('pendingOpenRoom');
      if (pendingRoomId) {
         _pendingSwRoomId = null;
         // pendingOpenRoom은 MobileGroupsView가 읽도록 남겨둠
         setTimeout(() => dispatchSwNavigation(pendingRoomId), 800);
      } else {
         // postMessage가 유실된 경우 (iOS가 앱 프로세스 종료 후 재시작 등):
         // SW에 pendingNavigateRoomId가 남아있을 수 있으므로 직접 요청
         setTimeout(() => {
            navigator.serviceWorker?.controller?.postMessage({ type: 'GET_PENDING_NAVIGATE' });
         }, 800);
      }
   }, [isLoggedIn, dispatchSwNavigation]);

   // 파비콘에 빨간 뱃지 그리기
   const updateFavicon = useCallback((count) => {
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
         ctx.drawImage(img, 0, 0, 64, 64);
         if (count > 0) {
            const text = count > 99 ? '99+' : String(count);
            const fontSize = text.length > 2 ? 20 : 26;
            ctx.font = `bold ${fontSize}px sans-serif`;
            const textWidth = ctx.measureText(text).width;
            const badgeSize = Math.max(28, textWidth + 12);
            const x = 64 - badgeSize / 2;
            const y = badgeSize / 2;
            ctx.beginPath();
            ctx.arc(x, y, badgeSize / 2, 0, Math.PI * 2);
            ctx.fillStyle = '#ef4444';
            ctx.fill();
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 2;
            ctx.stroke();
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(text, x, y + 1);
         }
         let link = document.querySelector("link[rel~='icon']");
         if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
         link.href = canvas.toDataURL('image/png');
      };
      img.src = '/heyheylogo.png';
   }, []);

   // ── FCM 토큰 등록 (로그인 후 1회 실행) ──
   useEffect(() => {
      if (!isLoggedIn) return;

      const setupFCMToken = async () => {
         if (_pushSubscribing) return;
         _pushSubscribing = true;
         try {
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
               console.warn('[FCM] ServiceWorker 또는 PushManager 미지원'); return;
            }

            // messagingReady를 await해서 isSupported() 레이스 컨디션 방지
            // iOS에서 isSupported()가 느리게 resolve되면 messaging이 null인 채로 실행될 수 있음
            const fcmMessaging = await messagingReady;
            if (!fcmMessaging) {
               console.warn('[FCM] Firebase Messaging 미지원 환경 (iOS 구버전 또는 일반 브라우저)'); return;
            }

            const permission = await Notification.requestPermission();
            if (permission !== 'granted') return;

            const registration = await navigator.serviceWorker.ready;

            const fcmToken = await getToken(fcmMessaging, {
               vapidKey: FCM_VAPID_KEY,
               serviceWorkerRegistration: registration,
            });
            if (!fcmToken) return;

            const currentUser = auth.currentUser;
            if (!currentUser) return;
            const idToken = await currentUser.getIdToken();
            const API = API_BASE_URL;

            await fetch(`${API}/api/push/fcm-token`, {
               method: 'POST',
               headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
               body: JSON.stringify({ token: fcmToken }),
            });
            updateBadge();
         } catch (err) {
            console.warn('[FCM] 토큰 설정 실패:', err.message);
         } finally {
            _pushSubscribing = false;
         }
      };

      setupFCMToken();

      // 알림 재활성화 시 토큰 재등록
      const handleResubscribe = () => setupFCMToken();
      window.addEventListener('requestPushResubscribe', handleResubscribe);

      // SW 업데이트 감지 시 토큰 재등록 + 배지 갱신
      const handleControllerChange = () => {
         setTimeout(() => setupFCMToken(), 3000);
         // 새 SW가 완전히 활성화된 후 배지 재계산 (controller 교체 직후 바로 호출하면 null일 수 있어 1초 지연)
         setTimeout(() => updateBadge(), 1000);
      };
      navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);

      return () => {
         window.removeEventListener('requestPushResubscribe', handleResubscribe);
         navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
      };
   }, [isLoggedIn]);

   // PWA 앱 아이콘 뱃지 (300ms 디바운스: 소켓+FCM BADGE_UPDATE 동시 호출 경쟁 조건 방지)
   const updateBadge = useCallback(() => {
      clearTimeout(_updateBadgeTimer.current);
      _updateBadgeTimer.current = setTimeout(_doUpdateBadge, 300);
   }, []); // eslint-disable-line react-hooks/exhaustive-deps
   const _doUpdateBadge = useCallback(async () => {
      try {
         const currentUser = auth.currentUser;
         if (!currentUser) return;
         const token = await currentUser.getIdToken();
         const res = await fetch(
            `${API_BASE_URL}/api/coordination/my-rooms`,
            { headers: { Authorization: `Bearer ${token}` } }
         );
         if (!res.ok) return;
         const data = await res.json();
         const rooms = [...(data.owned || []), ...(data.joined || [])];
         // 현재 열린 방은 뱃지 카운트에서 제외
         const currentRoomId = localStorage.getItem('currentRoomId');
         // 최근 읽은 방(10초 이내)도 제외 — markAsRead 완료 전 폴링이 구 데이터 덮어쓰는 것 방지
         let justReadStore = {};
         try { justReadStore = JSON.parse(localStorage.getItem('justReadRoomsStore') || '{}'); } catch {}
         const now = Date.now();
         const total = rooms.reduce((sum, r) => {
            if (currentRoomId && r._id === currentRoomId) {
               return sum;
            }
            const readAt = justReadStore[r._id];
            if (readAt && now - readAt < 5000) {
               return sum;
            }
            return sum + (r.unreadCount || 0);
         }, 0);
         // 방 목록 UI 실시간 업데이트용 이벤트
         window.dispatchEvent(new CustomEvent('roomListUpdated', { detail: { data } }));

         // 방 목록 소켓: 방 목록에 있을 때 새 메시지 실시간 감지
         if (roomListSocketRef.current) {
            rooms.forEach(r => roomListSocketRef.current.emit('join-room', r._id));
         }

         document.title = total > 0 ? `(${total}) AI Schedule` : 'AI Schedule';
         updateFavicon(total);

         if ('setAppBadge' in navigator) {
            if (total > 0) {
               navigator.setAppBadge(total);
            } else {
               navigator.clearAppBadge();
            }
         }
         // iOS PWA는 main thread의 setAppBadge가 홈화면 배지에 반영 안 됨
         // → SW에 메시지를 보내 SW 컨텍스트에서 setAppBadge 호출 (iOS 정책 우회)
         if (navigator.serviceWorker?.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'SET_BADGE', count: total });
         }
         // SW가 죽어있어 controller가 null일 때도 Cache를 직접 동기화
         // SW 재시작 후 push 수신 시 storedBadge가 정확한 값을 반환 → 배지 누적 버그 방지
         // (예: 3개 읽은 후 새 메시지 1개 → storedBadge 3 스테일 → 배지 4로 표시되는 버그)
         if ('caches' in window) {
            caches.open('sw-state-v1').then(c => {
               if (total > 0) {
                  c.put('/sw-badge-count', new Response(String(total)));
               } else {
                  c.delete('/sw-badge-count');
               }
            }).catch(() => {});
         }
      } catch (e) {
         console.warn('[updateBadge] 오류:', e.message);
      }
   }, []); // eslint-disable-line react-hooks/exhaustive-deps

   // userRef 최신화
   useEffect(() => { userRef.current = user; }, [user]);

   // 방 목록 실시간 업데이트용 소켓 (방 목록에 있을 때 새 메시지 즉시 반영)
   useEffect(() => {
      if (!isLoggedIn) return;
      const socket = io(API_BASE_URL, { transports: ['websocket', 'polling'] });
      roomListSocketRef.current = socket;
      // 소켓 연결/재연결 시 방 재가입 (네트워크 복구 후 실시간 배지 업데이트 보장)
      socket.on('connect', () => {
         updateBadge();
         const u = userRef.current;
         if (u) {
            socket.emit('user-online', {
               userId: u._id || u.id,
               name: u.firstName || u.name || '',
               email: u.email || '',
            });
         }
      });
      // 방 목록에 있을 때만 (특정 방 입장 중이 아닐 때) 실시간 갱신
      socket.on('chat-message', () => {
         if (!localStorage.getItem('currentRoomId')) {
            updateBadge();
         }
      });
      return () => {
         socket.disconnect();
         roomListSocketRef.current = null;
      };
   }, [isLoggedIn, updateBadge]);

   useEffect(() => {
      if (!isLoggedIn) return;

      // 앱 시작 시 SW stale currentRoomId 제거
      // 이전 세션에서 강제종료로 pagehide 미발화 → 잔존 Cache가 push를 억제하는 버그 방지
      if ('caches' in window) {
         caches.open('sw-state-v1').then(c => c.delete('/sw-current-room')).catch(() => {});
      }
      navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_CURRENT_ROOM' });

      updateBadge();

      // 화면 보일 때 5초, 숨겨질 때 30초 간격으로 polling
      let interval = setInterval(updateBadge, document.hidden ? 30000 : 5000);
      const onVisibility = () => {
         clearInterval(interval);
         interval = setInterval(updateBadge, document.hidden ? 30000 : 5000);
         if (document.hidden) {
            // 백그라운드 전환 시 SW의 stale currentRoomId 제거
            // CoordinationTab이 없는 화면(홈/달력/일정/설정 등)에서도 동작 보장
            navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_CURRENT_ROOM' });
            if ('caches' in window) {
               caches.open('sw-state-v1').then(c => c.delete('/sw-current-room')).catch(() => {});
            }
         }
         if (!document.hidden) {
            updateBadge();
            // 페이지가 foreground로 올 때 500ms 후 SW에 pending 방 이동 요청
            // (postMessage가 suspended 앱에 유실된 경우 대비)
            setTimeout(() => {
               if (_pendingSwRoomId) {
                  // 모듈레벨 리스너가 이미 캡처한 경우
                  const rid = _pendingSwRoomId;
                  _pendingSwRoomId = null;
                  dispatchSwNavigation(rid);
               } else {
                  navigator.serviceWorker?.controller?.postMessage({ type: 'GET_PENDING_NAVIGATE' });
               }
            }, 500);
         }
      };
      document.addEventListener('visibilitychange', onVisibility);

      // SW에서 push 수신 시 즉시 뱃지 갱신 + 방 이동 처리
      const onSWMessage = (event) => {
         if (event.data?.type === 'BADGE_UPDATE') updateBadge();
         // Android: 알림 dismiss 후 SW가 badge 재설정 요청 → 메인 스레드에서 setAppBadge 재호출
         if (event.data?.type === 'REASSERT_BADGE') {
            const { count } = event.data;
            if ('setAppBadge' in navigator && count > 0) {
               navigator.setAppBadge(count).catch(() => {});
            }
         }
         if (event.data?.type === 'NAVIGATE_TO_ROOM') {
            const { roomId } = event.data;
            _pendingSwRoomId = null; // 모듈레벨 pending 클리어 (중복 처리 방지)
            if (roomId) localStorage.setItem('pendingOpenRoom', roomId);
            navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_PENDING_NAVIGATE' });
            dispatchSwNavigation(roomId);
         }
         // 새 SW 활성화 시 구 JS 번들로 실행 중인 페이지를 리로드 → 새 코드 적용
         if (event.data?.type === 'SW_UPDATED') {
            window.location.reload();
         }
      };
      if ('serviceWorker' in navigator) {
         navigator.serviceWorker.addEventListener('message', onSWMessage);
      }

      // GroupChat 소켓에서 새 메시지 수신 시 즉시 갱신
      const onNewMessage = () => updateBadge();
      window.addEventListener('unread-count-changed', onNewMessage);

      return () => {
         clearInterval(interval);
         document.removeEventListener('visibilitychange', onVisibility);
         window.removeEventListener('unread-count-changed', onNewMessage);
         if ('serviceWorker' in navigator) {
            navigator.serviceWorker.removeEventListener('message', onSWMessage);
         }
      };
   }, [isLoggedIn, updateBadge]);

   // PWA 설치 프롬프트 캡처: beforeinstallprompt 이벤트를 저장해두었다가
   // 설정 화면의 "앱 설치" 버튼 클릭 시 사용 → 정식 설치 완료 후 share_target 등록됨
   useEffect(() => {
      // index.html에서 미리 캡처된 이벤트 확인 (React 로드 전에 발생한 경우)
      if (window._installPrompt) {
         setInstallPrompt(window._installPrompt);
         window._installPrompt = null;
      }
      const handler = (e) => {
         e.preventDefault();
         setInstallPrompt(e);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
   }, []);

   const handleToggleVoiceRecognition = useCallback(() => {
      setIsVoiceRecognitionEnabled(prev => {
         const newValue = !prev;
         localStorage.setItem('voiceRecognitionEnabled', JSON.stringify(newValue));
         return newValue;
      });
   }, []);

   return (
      <ToastProvider>
      {/* 초대 가입 후 설치 강제 게이트: 앱 설치 전까지 모든 콘텐츠 차단 */}
      {showInstallGate && (
         <InstallGateScreen
            installPrompt={installPrompt}
            onInstallPromptUsed={() => setInstallPrompt(null)}
         />
      )}
      {!showInstallGate && showInstallOverlay && (
         <InstallPromptOverlay
            installPrompt={installPrompt}
            onInstallPromptUsed={() => setInstallPrompt(null)}
            onDismiss={() => setShowInstallOverlay(false)}
         />
      )}
      <Router>
         <SWNavigationHandler isLoggedIn={isLoggedIn} />
         <AdminRedirectHandler isLoggedIn={isLoggedIn} user={user} />
         <PendingCodeNavigator code={pendingJoinCode} overlayVisible={showInstallOverlay} onNavigated={handleCodeNavigated} />
         <Routes>
            <Route path="/auth" element={
               (() => {
                  const params = new URLSearchParams(window.location.search);
                  const hasCalendarCallback = params.get('calendarConnected') || params.get('calendarError');
                  // replace: 로그인 후 /auth 히스토리 엔트리를 교체 → 홈에서 스와이프 시 로그인 화면으로 돌아가는 버그 방지
                  if (isLoggedIn && !hasCalendarCallback) {
                     // pendingInviteCode는 isLoggedIn effect + PendingCodeNavigator가 처리
                     if (user?.role === 'admin') return <Navigate to="/admin" replace />;
                     return <Navigate to="/" replace />;
                  }
                  return <AuthScreen onLoginSuccess={handleLoginSuccess} />;
               })()
            } />
            <Route path="/invite/:inviteCode" element={<InvitePage
               installPrompt={installPrompt}
               onInstallPromptUsed={() => setInstallPrompt(null)}
            />} />
            {/* 관리자 대시보드 */}
            <Route path="/admin" element={
               isLoggedIn && user?.role === 'admin'
                  ? <AdminDashboard user={user} />
                  : <Navigate to={isLoggedIn ? '/' : '/auth'} replace />
            } />
            {/* Maps가 필요한 나머지 라우트: LoadScript 로드 완료 후 렌더링 */}
            <Route path="*" element={
               <MapLoadedRoutes
                  isLoggedIn={isLoggedIn}
                  user={user}
                  isVoiceEnabled={isVoiceRecognitionEnabled}
                  setIsVoiceEnabled={handleToggleVoiceRecognition}
                  installPrompt={installPrompt}
                  onInstallPromptUsed={() => setInstallPrompt(null)}
               />
            } />
         </Routes>
      </Router>
      </ToastProvider>
   );
}

export default App;
